<template>
  <div
    v-if="showPicker"
    class="mb-2"
    data-testid="cart-saved-address-picker"
  >
    <v-select
      :items="options"
      :model-value="selectedId"
      item-title="title"
      item-value="id"
      label="Use a saved address"
      density="comfortable"
      hide-details="auto"
      data-testid="cart-saved-address-select"
      @update:model-value="onPick"
    />
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
/**
 * Pick a saved address into the storefront cart's shipping form.
 *
 * WHY THIS EXISTS
 * ===============
 * `StorefrontSavedAddresses.vue` already renders a shopper's address book,
 * but it is a full-page MANAGEMENT surface (add / edit / delete / set
 * default) mounted only at `/shop/:slug/addresses`. The cart never
 * referenced it, so a shopper who had saved a default address still had to
 * retype it at checkout -- and nothing prefilled it.
 *
 * WHY COMPOSE RATHER THAN REUSE THAT COMPONENT DIRECTLY
 * =====================================================
 * Dropping the page component into the cart would bring its Add / Edit /
 * Delete / Set-default controls and its `saved-addresses-page` container
 * onto the money path -- a destructive surface inside checkout, and a
 * second place those controls would have to be kept correct. It would also
 * force a contract change on a component that is on the money path
 * elsewhere.
 *
 * So this is a separate, read-only picker that SHARES the logic rather than
 * the markup: `sortAddressesDefaultFirst` and `formatAddressLine` come from
 * `savedAddressesHelpers.js`, the same module the page uses, so the ordering
 * and the address line cannot drift between the two surfaces.
 * `StorefrontSavedAddresses.vue` is unmodified.
 *
 * WHAT IT EMITS
 * =============
 * `select` carries only the six address fields the cart's `shopperLocation`
 * holds. It deliberately does NOT carry `id`, `account_id`, `label`,
 * `is_default` or `_jv`: the cart is copying VALUES into a form, not
 * adopting a server-owned row. Not passing the identity also means picking a
 * saved address can never be mistaken by a downstream writer for an
 * instruction to re-parent or mutate that row -- the own-row scoping and the
 * cross-tenant re-parenting guard added in PR #3057 stay untouched, because
 * this component never writes an address at all. It is GET-only.
 */
import {
  formatAddressLine,
  sortAddressesDefaultFirst,
} from "./savedAddressesHelpers";

export default {
  name: "CartSavedAddressPicker",

  emits: ["select"],

  data() {
    return {
      addresses: [],
      loading: false,
      /** Set once the default has been applied, so it happens exactly once. */
      prefilled: false,
      selectedId: null,
    };
  },

  computed: {
    /**
     * Hide the control entirely when there is nothing to pick.
     *
     * An empty picker is worse than no picker: it implies the shopper has
     * saved addresses and that none of them matched.
     */
    showPicker() {
      return !this.loading && this.options.length > 0;
    },

    sortedAddresses() {
      return sortAddressesDefaultFirst(this.addresses);
    },

    options() {
      return this.sortedAddresses.map((address) => ({
        id: this.addressId(address),
        title: this.optionTitle(address),
      }));
    },
  },

  async created() {
    await this.load();
  },

  methods: {
    addressId(address) {
      if (!address) return null;
      if (address._jv && address._jv.id != null) return address._jv.id;
      if (address.id != null) return address.id;
      return null;
    },

    /** "Home -- Jake Savin, 601 E Rollins St, Orlando, FL 32803, US" */
    optionTitle(address) {
      const line = formatAddressLine(address);
      const label = address && address.label ? String(address.label).trim() : "";
      return label ? `${label} -- ${line}` : line;
    },

    /** The six fields the cart's `shopperLocation` actually holds. */
    toShopperLocation(address) {
      return {
        street_and_number: address.street_and_number || "",
        suite_unit_etc: address.suite_unit_etc || "",
        city: address.city || "",
        region: address.region || "",
        zip_code: address.zip_code || "",
        country: address.country || "US",
      };
    },

    async load() {
      this.loading = true;
      try {
        // jv/get takes a bare string. A single-element array leaves config
        // undefined and crashes inside jsonapi-vuex (the PR #1836 crash
        // class) -- same call shape as StorefrontSavedAddresses.vue.
        const result = await this.$store.dispatch("jv/get", "addresses");
        const fetched = Object.values(result || {});

        // Defense in depth (CLAUDE.md Principle 5), mirroring the page
        // component. The backend already scopes /api/addresses/ to the
        // authenticated account; filtering again here means a future
        // backend regression cannot surface another shopper's address as a
        // pickable option on a checkout form.
        const myAccountId = this.$store.state.account?._jv?.id;
        this.addresses = myAccountId
          ? fetched.filter(
              (a) =>
                String(a?.account?._jv?.id ?? a?.account_id ?? "") ===
                String(myAccountId)
            )
          : fetched;
      } catch (err) {
        // A missing address book must never block checkout -- the shopper
        // can always type an address. Fail quiet and hide the control.
        this.addresses = [];
        // eslint-disable-next-line no-console
        console.error("Failed to load saved addresses for the cart", describeError(err));
      } finally {
        this.loading = false;
      }
      this.applyDefault();
    },

    /**
     * Prefill from the shopper's DEFAULT address, once.
     *
     * Only the address flagged `is_default` prefills. Picking the first of
     * several non-default addresses would be a guess, and a wrong guess here
     * silently changes where an order ships.
     */
    applyDefault() {
      if (this.prefilled) return;
      const preferred = this.sortedAddresses.find((a) => a && a.is_default);
      if (!preferred) return;
      this.prefilled = true;
      this.selectedId = this.addressId(preferred);
      this.$emit("select", this.toShopperLocation(preferred));
    },

    onPick(id) {
      this.selectedId = id;
      const picked = this.sortedAddresses.find(
        (a) => String(this.addressId(a)) === String(id)
      );
      if (!picked) return;
      // A manual pick is a decision -- it must not be undone by a later
      // default prefill.
      this.prefilled = true;
      this.$emit("select", this.toShopperLocation(picked));
    },
  },
};
</script>
